<template>
  <div>
    <v-sheet
      v-if="loading"
      :color="'grey lighten-4'"
      class="pa-3"
    >
      <v-skeleton-loader type="card-heading, table-row-divider">
      </v-skeleton-loader>
    </v-sheet>
    <naj-box
      v-else
      :title="local.bankruptcy.subRequests.objectionRequest.title"
    >
      <v-row>
        <v-col cols="3">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="formModel.judgementCode"
            :label="local.bankruptcy.subRequests.objectionRequest.judgementCode"
            :items="judgements"
            item-value="code"
            item-text="code"
            :validates="`required`"
            :disabled="isAutoSelected|| isViewMode || isEditMode"
            @change="setSelectedJudgment"
          ></naj-input>
        </v-col>

        <v-col
          cols="1"
          class="judgement-deed"
        >
          <naj-button
            :disabled="!selectedJudgment.objectKey"
            @click="getJudgementDeed"
          >
            {{ local.bankruptcy.subRequests.objectionRequest.displayJudgement }}
          </naj-button>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <naj-input
            :type-id="formFieldType.textarea"
            :label="local.bankruptcy.subRequests.objectionRequest.judgementText"
            :value.sync="selectedJudgment.judgementText"
            disabled
            validates="max:50000"
          ></naj-input>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="memorandum-challenge-alert-container my-4">
            <span class="message">
              <v-icon color="#f6c645">
                mdi-alert
              </v-icon>
              {{ local.bankruptcy.subRequests.objectionRequest.memorandumChallengeAlert }}
            </span>
          </div>

          <naj-input
            :type-id="formFieldType.textarea"
            :label="local.bankruptcy.subRequests.objectionRequest.memorandumChallenge"
            :value.sync="formModel.verdictObjectionText"
            validates="required|max:5000"
            :disabled="isViewMode"
          ></naj-input>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-if="formModel.objectionFileId"
          cols="3"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.bankruptcy.subRequests.objectionRequest.objectionNoteAttachment }}
          </p>
          <attachment-card
            :file="{ ...uploadedFile }"
            :show-delete-button="!isViewMode"
            @deleteAttachment="showDeleteObjectionNoteModal"
            @viewAttachment="viewAttachment"
          ></attachment-card>
        </v-col>
        <v-col
          v-else
          class="objection-note-attachment"
          cols="12"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.bankruptcy.subRequests.objectionRequest.addObjectionNoteAttachment }}
          </p>
          <load-local-attachment
            :max-file-size="5"
            :upload-disabled="isViewMode"
            @fileSelected="fileSelected"
          >
          </load-local-attachment>
        </v-col>
      </v-row>
    </naj-box>

    <case-judgement-modal
      :selected-judgement="selectedJudgment"
      :modal-visibility="showCaseJudgementModal"
      @modalClose="closeCaseJudgementModal"
    ></case-judgement-modal>

    <render-attachment-modal
      :attachment-file="uploadedFile"
      :modal-visibility="showViewAttachmentModal"
      @modalClose="hideViewAttachmentModal"
    >
    </render-attachment-modal>

    <naj-confirmation
      :show-modal="showDeleteObjectionNoteAttachment"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.bankruptcy.subRequests.objectionRequest.removeObjectionNoteAttachmentMessage"
      :ok-btn-label="local.common.remove"
      @ok="confirmDeleteObjectionNote"
      @cancel="cancelDeleteObjectionNote"
    ></naj-confirmation>
  </div>
</template>

<script>
import { mode } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState } = createNamespacedHelpers('LookupsModule'); // todo REMOVE
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapActions } = createNamespacedHelpers('CasesModule');
const { mapActions: mapBankruptcyActions } = createNamespacedHelpers('BankruptcyModule');

import CaseJudgementModal from '@/modules/cases/case-judgement-modal';
import LoadLocalAttachment from '@/components/shared/attachment-modal/load-local-attachment.vue';
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';
import AttachmentCard from '@/components/shared/attachment-modal/attachment-card';
import { subRequestType } from '@/modules/bankruptcy/helpers/enum';

export default {
  name: 'BankruptcyObjectionRequest',
  inject: ['$validator'],
  components: {
    CaseJudgementModal,
    LoadLocalAttachment,
    RenderAttachmentModal,
    AttachmentCard,
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    requiredData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      judgements: [],
      showCaseJudgementModal: false,
      showDeleteObjectionNoteAttachment: false,
      showViewAttachmentModal: false,
      selectedJudgment: {},
      isAutoSelected: false,
      subRequestType,
      loading: true,
    };
  },
  computed: {
    ...mapLUTState([]),
    ...mapGlobalState(['userInfo']),
    isViewMode() {
      return this.formModel && this.formModel.mode === mode.view;
    },
    isEditMode() {
      return this.formModel && this.formModel.mode === mode.update;
    },
    isNewMode() {
      return this.$route.params.subRequestObjectKey == undefined || this.$route.params.subRequestObjectKey == null;
    },
    uploadedFile() {
      if (this.formModel.objectionFileId) {
        return {
          name: this.formModel.objectionFileName,
          sharePointId: this.formModel.objectionFileId,
          isImage: this.formModel.objectionFileType.startsWith('image'),
          fileType: this.formModel.objectionFileType,
        };
      }
      return {};
    },
  },
  watch: {},
  mounted() {
    this.loadData();
    this.formModel.subRequestTypeId = subRequestType.VerdictObjectionRequest;
  },
  created() {
  },
  methods: {
    ...mapBankruptcyActions(['getMainRequestJudgementsSummary', 'getJudgementsBySubRequestObjectKey']),
    loadData() {
      this.loading = true;
      this.isAutoSelected = false;
      const method = this.requiredData.isChild ? this.getJudgementsBySubRequestObjectKey(this.formModel.parentSubRequestObjectKey) : this.getMainRequestJudgementsSummary(this.formModel.mainRequest.objectKey);
      method.then((res) => {
        this.judgements = res.data;
        if ((this.judgements || []).length == 0) {
          this.toaster.error(this.local.bankruptcy.subRequests.objectionRequest.noJudgments);
          return;
        }
        const judgementCode = this.$route.query.judgementCode;
        if (this.isValidJudgementCode(judgementCode)) {
          this.$set(this.formModel, 'judgementCode', judgementCode);
          this.isAutoSelected = true;
        }
        this.setSelectedJudgment();
      })
        .finally(() => {
          this.loading = false;
        });
    },
    ...mapActions([
      'uploadAttachment',
    ]),
    isValidJudgementCode(code) {
      return (this.judgements || []).find(a => a.code == code) != null;
    },
    setSelectedJudgment() {
      const selectedJudgment = this.judgements.length == 1 ? this.judgements[0] : (this.judgements || []).find(a => a.code == this.formModel.judgementCode) || {};
      this.$set(this, 'selectedJudgment', selectedJudgment);
      this.$set(this.formModel, 'judgementCode', selectedJudgment.code);
      this.$set(this.formModel, 'judgementId', selectedJudgment.id);
    },
    getJudgementDeed() {
      this.showCaseJudgementModal = true;
    },
    closeCaseJudgementModal() {
      this.showCaseJudgementModal = false;
    },
    fileSelected({ selectedAttachment: attachment }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');
        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            this.$set(this.formModel, 'objectionFileName', attachment.name);
            this.$set(this.formModel, 'objectionFileId', data);
            this.$set(this.formModel, 'objectionFileType', attachment.fileObject.type);
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    showDeleteObjectionNoteModal() {
      this.showDeleteObjectionNoteAttachment = true;
    },
    confirmDeleteObjectionNote() {
      this.$delete(this.formModel, 'objectionFileName');
      this.$delete(this.formModel, 'objectionFileId');
      this.$delete(this.formModel, 'objectionFileType');
      this.cancelDeleteObjectionNote();
    },
    cancelDeleteObjectionNote() {
      this.showDeleteObjectionNoteAttachment = false;
    },
    viewAttachment() {
      this.showViewAttachmentModal = true;
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.judgement-deed {
  display: flex;
  align-items: center;
  justify-content: center;
}
.memorandum-challenge-alert-container {
  .alert-notification {
    display: block;
    color: #f6c645;
    font-size: 14px;
  }
  .message {
    font-size: 16px;
    color: #faad14;
  }
}

.objection-judgment-confirm-text {
  color: darkred;
  font-size: 1rem;
  margin-bottom: 0;
}

.objection-note-attachment {
  display: flex;
  .objection-judgment-confirm-text {
    margin-left: 10px;
  }
  .uplaod-attachments-button .img-actions {
    width: 100% !important;
  }
}

::v-deep .attachment-item {
  cursor: pointer;
}
</style>